import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, Link2,
  Heading1, Heading2, Heading3, Quote, Code, AlignLeft,
  AlignCenter, AlignRight, Undo, Redo, Image as ImageIcon, Eraser,
} from 'lucide-react';
import { Button } from '../ui/button';
import { safeHtml } from '../../utils/safeHtml';

/**
 * Lightweight in-house WYSIWYG editor for the CMS "Rich Content" block.
 * Uses contentEditable + document.execCommand (still supported in evergreen browsers).
 * Stores output as HTML string for maximum WordPress-like fidelity.
 *
 * Security: every value coming IN and going OUT of the contentEditable
 * surface passes through DOMPurify so admins can't accidentally paste
 * (or be tricked into pasting) inline scripts / event handlers.
 */
export function RichTextEditor({ value, onChange, placeholder = 'Write your content…' }) {
  const editorRef = useRef(null);
  const [showHtml, setShowHtml] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value || '');

  // Sync external value -> editor (only when changed externally, to avoid cursor jumps).
  // Sanitize before injecting into the DOM.
  useEffect(() => {
    if (!editorRef.current) return;
    const clean = safeHtml(value || '');
    if (editorRef.current.innerHTML !== clean) {
      editorRef.current.innerHTML = clean;
    }
    setHtmlValue(clean);
  }, [value]);

  const exec = useCallback((cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    onChange?.(safeHtml(editorRef.current?.innerHTML || ''));
  }, [onChange]);

  const handleInput = () => {
    const html = safeHtml(editorRef.current?.innerHTML || '');
    setHtmlValue(html);
    onChange?.(html);
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (https:// or / for internal):');
    if (url) exec('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Image URL:');
    if (url) exec('insertImage', url);
  };

  const clearFormatting = () => {
    exec('removeFormat');
  };

  return (
    <div className="rounded-lg border border-white/15 bg-[#000B18] overflow-hidden" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-white/10 bg-[#041A3B]">
        <ToolBtn onClick={() => exec('undo')} title="Undo"><Undo className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('redo')} title="Redo"><Redo className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => exec('formatBlock', '<h1>')} title="Heading 1"><Heading1 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<h2>')} title="Heading 2"><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<h3>')} title="Heading 3"><Heading3 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<p>')} title="Paragraph"><span className="text-xs">¶</span></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => exec('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Underline"><Underline className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bulleted list"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<blockquote>')} title="Quote"><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<pre>')} title="Code block"><Code className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Align left"><AlignLeft className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Align center"><AlignCenter className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyRight')} title="Align right"><AlignRight className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={insertLink} title="Insert link"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={insertImage} title="Insert image"><ImageIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={clearFormatting} title="Clear formatting"><Eraser className="w-4 h-4" /></ToolBtn>
        <div className="ml-auto">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setShowHtml(v => !v)}
            className="h-7 text-xs text-[#A0B4CC] hover:text-[#00E5FF]"
            data-testid="toggle-html-source"
          >
            {showHtml ? 'Visual' : 'HTML'}
          </Button>
        </div>
      </div>

      {/* Editor surface */}
      {showHtml ? (
        <textarea
          value={htmlValue}
          onChange={(e) => { setHtmlValue(e.target.value); onChange?.(e.target.value); }}
          className="w-full min-h-[200px] p-3 bg-[#000B18] text-[#A0B4CC] font-mono text-xs outline-none resize-y"
          spellCheck={false}
          data-testid="rich-text-html-source"
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          data-placeholder={placeholder}
          className="rich-text-editor-content min-h-[200px] max-h-[500px] overflow-y-auto p-3 text-white prose prose-invert max-w-none focus:outline-none"
          data-testid="rich-text-editor-content"
          style={{ wordBreak: 'break-word' }}
        />
      )}
    </div>
  );
}

function ToolBtn({ children, onClick, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="p-1.5 rounded text-[#A0B4CC] hover:bg-white/10 hover:text-white transition-colors"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 w-px h-5 bg-white/15" aria-hidden />;
}

export default RichTextEditor;
